import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trash2, Star, Eye, EyeOff, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { API } from '../App';

/**
 * Admin testimonial CRUD. Only the platform owner adds REAL testimonials —
 * never auto-seeded. Use ratings 1-5, optional role/avatar/source URL.
 */
export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: '', role: '', quote: '', avatar_url: '', rating: 5, source_url: '', approved: true,
  });
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/testimonials`);
      setItems(r.data.testimonials || []);
    } catch (e) {
      toast.error('Could not load testimonials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.quote.trim()) {
      toast.error('Name and quote are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/testimonials`, form);
      toast.success('Testimonial added');
      setForm({ name: '', role: '', quote: '', avatar_url: '', rating: 5, source_url: '', approved: true });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this testimonial? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`);
      toast.success('Deleted');
      reload();
    } catch (e) {
      toast.error('Could not delete');
    }
  };

  const toggleApprove = async (id, approved) => {
    try {
      await axios.put(`${API}/admin/testimonials/${id}/approve?approved=${!approved}`);
      reload();
    } catch (e) {
      toast.error('Could not update');
    }
  };

  return (
    <div className="space-y-6" data-testid="testimonials-admin">
      <div>
        <h2 className="text-2xl font-heading font-medium">Testimonials</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Add <strong>real</strong> customer/vendor quotes only. They appear on the homepage when approved.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add Real Testimonial
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input placeholder="Name (e.g. Sarah O.)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="testimonial-name" />
              <Input placeholder="Role (e.g. Founding Vendor, Customer)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="testimonial-role" />
            </div>
            <Textarea placeholder='The actual quote (e.g. "I love how this platform feels rooted in faith.")' value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} rows={3} required data-testid="testimonial-quote" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input placeholder="Avatar URL (optional)" value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} data-testid="testimonial-avatar" />
              <Input placeholder="Source URL (optional — original review link)" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} data-testid="testimonial-source" />
              <select
                className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={form.rating}
                onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}
                data-testid="testimonial-rating"
              >
                <option value={5}>★★★★★ (5)</option>
                <option value={4}>★★★★ (4)</option>
                <option value={3}>★★★ (3)</option>
                <option value={2}>★★ (2)</option>
                <option value={1}>★ (1)</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={form.approved}
                onChange={(e) => setForm({ ...form, approved: e.target.checked })}
              />
              Show on homepage immediately
            </label>
            <Button type="submit" disabled={submitting} data-testid="testimonial-submit">
              {submitting ? 'Saving…' : 'Add Testimonial'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All Testimonials ({items.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              <p className="text-sm">No testimonials yet.</p>
              <p className="text-xs mt-2">The homepage section stays hidden until you add real ones above.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border/40">
              {items.map((t) => (
                <li key={t.testimonial_id} className="py-4 flex items-start gap-3" data-testid={`admin-testimonial-${t.testimonial_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium text-foreground text-sm">{t.name}</span>
                      {t.role && <span className="text-xs text-muted-foreground">— {t.role}</span>}
                      {Number.isInteger(t.rating) && (
                        <span className="inline-flex items-center text-xs text-amber-600">
                          <Star className="h-3 w-3 fill-amber-500" /> {t.rating}
                        </span>
                      )}
                      <Badge variant={t.approved ? 'default' : 'secondary'} className="text-xs">
                        {t.approved ? 'Live' : 'Hidden'}
                      </Badge>
                    </div>
                    <p className="text-sm text-foreground/80 italic">&ldquo;{t.quote}&rdquo;</p>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => toggleApprove(t.testimonial_id, t.approved)} data-testid={`testimonial-toggle-${t.testimonial_id}`}>
                      {t.approved ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => remove(t.testimonial_id)} data-testid={`testimonial-delete-${t.testimonial_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
